import { ArrowLeft, ArrowRight, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addProject, deleteProject } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
};

const MAX_LIMITS: Record<string, number> = {
  projectName: 50,
  role: 50,
  technologies: 100,
  description: 200
};

const isValidUrl = (url = '') => {
  if (!url.trim()) return true;
  return /^https?:\/\/.+\..+/.test(url.trim());
};

const isValidGithubUrl = (url = '') => {
  if (!url.trim()) return true;
  return /^https?:\/\/(www\.)?github\.com\/[\w-]+\/?/.test(url.trim());
};

const ProjectsForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack
}) => {
  const dispatch = useDispatch();

  const projects = useSelector((state: any) => state.root.resume.projects);

  const [form, setForm] = useState({
    projectName: '',
    role: '',
    technologies: '',
    projectUrl: '',
    githubUrl: '',
    startDate: '',
    endDate: '',
    description: ''
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (MAX_LIMITS[name] && value.length > MAX_LIMITS[name]) return;

    const dateFields = ['startDate', 'endDate'];
    const formattedValue =
      dateFields.includes(name) && value
        ? new Date(value).toISOString()
        : value;

    setForm({
      ...form,
      [name]: formattedValue
    });
  };

  const handleBlur = (e: any) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const projectUrlValid = isValidUrl(form.projectUrl);
  const githubUrlValid = isValidGithubUrl(form.githubUrl);

  const isSaveValid =
    form.projectName?.trim() && form.role?.trim() && form.technologies?.trim();

  const saveProject = () => {
    if (!isSaveValid) return;

    dispatch(addProject(form));

    setForm({
      projectName: '',
      role: '',
      technologies: '',
      projectUrl: '',
      githubUrl: '',
      startDate: '',
      endDate: '',
      description: ''
    });
    setTouched({});
  };

  const CharCount = ({
    value,
    max
  }: {
    value: string | undefined;
    max: number;
  }) => {
    const len = value?.length || 0;
    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>
            <h1 className='text-2xl font-semibold'>{stepName}</h1>
            <p className='mt-1 text-sm text-gray-400'>
              Highlight your best work
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        {/* FORM */}
        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>+ Add New Project</h3>

          {/* project name + role */}
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Project Name <span className='text-red-500'>*</span>{' '}
                </label>
                <CharCount value={form.projectName} max={50} />
              </div>
              <input
                name='projectName'
                value={form.projectName}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={50}
                placeholder='Enter project title'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.projectName && !form.projectName?.trim()
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.projectName && !form.projectName?.trim() && (
                <p className='mt-1 text-xs text-red-500'>
                  Project name is required
                </p>
              )}
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Role <span className='text-red-500'>*</span>
                </label>
                <CharCount value={form.role} max={50} />
              </div>
              <input
                name='role'
                value={form.role}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={50}
                placeholder='Your role'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.role && !form.role?.trim()
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.role && !form.role?.trim() && (
                <p className='mt-1 text-xs text-red-500'>Role is required</p>
              )}
            </div>
          </div>

          {/* technologies */}
          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>
                Technologies <span className='text-red-500'>*</span>
              </label>
              <CharCount value={form.technologies} max={100} />
            </div>
            <input
              name='technologies'
              value={form.technologies}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={100}
              placeholder='Eg: Angular, Firebase'
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                touched.technologies && !form.technologies?.trim()
                  ? 'border-red-400 focus:border-red-400'
                  : 'focus:border-gray-300'
              }`}
            />
            {touched.technologies && !form.technologies?.trim() && (
              <p className='mt-1 text-xs text-red-500'>
                Technologies is required
              </p>
            )}
          </div>

          {/* project url + github url */}
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium'>Project URL</label>
              <input
                name='projectUrl'
                value={form.projectUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Enter project URL'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.projectUrl && !projectUrlValid
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.projectUrl && !projectUrlValid ? (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid URL (e.g., https://your-project.com)
                </p>
              ) : (
                <p className='mt-1 text-xs text-gray-400'>
                  e.g., https://your-project.com
                </p>
              )}
            </div>

            <div>
              <label className='text-sm font-medium'>GitHub URL</label>
              <input
                name='githubUrl'
                value={form.githubUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Enter GitHub URL'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.githubUrl && !githubUrlValid
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.githubUrl && !githubUrlValid ? (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid GitHub URL (e.g.,
                  https://github.com/your-repo)
                </p>
              ) : (
                <p className='mt-1 text-xs text-gray-400'>
                  e.g., https://github.com/your-repo
                </p>
              )}
            </div>
          </div>

          {/* start date + end date */}
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium'>Start Date</label>
              <input
                type='date'
                name='startDate'
                value={form.startDate?.split('T')[0] || ''}
                onChange={handleChange}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <label className='text-sm font-medium'>End Date</label>
              <input
                type='date'
                name='endDate'
                value={form.endDate?.split('T')[0] || ''}
                onChange={handleChange}
                min={form.startDate?.split('T')[0] || ''}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>
          </div>

          {/* description */}
          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Description</label>
              <CharCount value={form.description} max={200} />
            </div>
            <textarea
              name='description'
              value={form.description}
              onChange={handleChange}
              maxLength={200}
              placeholder='Describe project'
              rows={3}
              className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
            />
          </div>

          <Button
            label='+ Save Project'
            handleClickBtn={saveProject}
            variant='contained'
            disabled={!isSaveValid}
            classes='mt-5'
          />
        </div>

        {/* ADDED PROJECTS */}
        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>Added Projects</h3>

          {projects.length === 0 ? (
            <p className='text-sm text-gray-400'>No projects added yet.</p>
          ) : (
            <div className='space-y-3'>
              {projects.map((project: any) => (
                <div
                  key={project.projectName}
                  className='flex items-center justify-between rounded-lg border p-3'
                >
                  <div>
                    <p className='font-medium'>{project.projectName}</p>
                    <p className='text-xs text-gray-500'>
                      {project.technologies} &bull; {project.role}
                    </p>
                  </div>

                  <button
                    onClick={() => dispatch(deleteProject(project.projectName))}
                    className='text-xs text-red-500'
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* footer */}
        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectsForm;
